import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, Building2, ArrowLeft, Clock, Tag } from 'lucide-react';
import { toast } from './ui/sonner';
import { getImpersonatedOrg, stopImpersonation, onImpersonationChange } from '../utils/impersonation';
import { apiCall } from '../utils/api';

function useCountdown(expiresAtIso) {
  const [remainingMs, setRemainingMs] = useState(() => {
    if (!expiresAtIso) return null;
    return new Date(expiresAtIso).getTime() - Date.now();
  });
  useEffect(() => {
    if (!expiresAtIso) return undefined;
    const t = setInterval(() => {
      setRemainingMs(new Date(expiresAtIso).getTime() - Date.now());
    }, 1000);
    return () => clearInterval(t);
  }, [expiresAtIso]);
  return remainingMs;
}

function fmtRemaining(ms) {
  if (ms == null) return '';
  if (ms <= 0) return 'expired';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
}

export default function SupportModeBanner() {
  const [org, setOrg] = useState(getImpersonatedOrg());
  const [session, setSession] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const off = onImpersonationChange((next) => setOrg(next));
    return off;
  }, []);

  const load = useCallback(async () => {
    if (!org) { setSession(null); return; }
    try {
      const r = await apiCall('/api/support-mode/active-session');
      setSession(r.session || null);
    } catch { /* ignore */ }
  }, [org]);

  useEffect(() => {
    load();
    if (!org) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [org, load]);

  const remainingMs = useCountdown(session?.expires_at);

  if (!org) return null;

  const exit = async () => {
    await stopImpersonation();
    toast.success('Exited Support Mode');
    navigate('/admin');
  };

  const roleLabel = org.role === 'agent' ? 'Agency' : 'Employer';
  const modeLabel = session?.mode === 'forced' ? 'Forced entry' :
                    session?.mode === 'consent' ? 'Consent granted' :
                    session?.mode === 'legacy' ? 'Legacy session' : 'Support Mode';
  const scopes = (session?.scopes || ['all']);
  const scopesLabel = scopes.includes('all') ? 'Full access' : scopes.join(' · ');
  const countdown = fmtRemaining(remainingMs);
  const soonExpiring = remainingMs != null && remainingMs < 5 * 60 * 1000;

  return (
    <div
      className="sticky top-0 z-50 bg-gradient-to-r from-rose-600 via-red-600 to-orange-600 text-white shadow-md"
      data-testid="support-mode-banner"
    >
      <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-1.5 rounded-md bg-white/15 ring-1 ring-white/20 flex-shrink-0">
            <ShieldAlert className="w-4 h-4" />
          </div>
          <div className="text-sm leading-tight min-w-0">
            <p className="font-bold flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider bg-white/20 px-1.5 py-0.5 rounded">
                {modeLabel}
              </span>
              <span className="hidden sm:inline">— Acting as</span>
            </p>
            <p className="text-xs text-white/90 truncate flex items-center gap-1.5">
              <Building2 className="w-3 h-3 flex-shrink-0" />
              <span className="font-semibold truncate">{org.company_name || org.full_name}</span>
              <span className="hidden md:inline text-white/70">· {roleLabel}</span>
              <span className="hidden lg:inline text-white/70">· {org.email}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {session?.expires_at && (
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold ${
                soonExpiring ? 'bg-yellow-300 text-yellow-900 animate-pulse' : 'bg-white/15 ring-1 ring-white/20'
              }`}
              data-testid="support-mode-countdown"
            >
              <Clock className="w-3 h-3" /> {countdown}
            </span>
          )}
          <span
            className="hidden md:inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-white/15 ring-1 ring-white/20"
            data-testid="support-mode-scopes"
          >
            <Tag className="w-3 h-3" /> {scopesLabel}
          </span>
          <button
            onClick={exit}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm ring-1 ring-white/20 rounded-lg text-xs font-bold transition"
            data-testid="exit-support-mode-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Exit
          </button>
        </div>
      </div>
    </div>
  );
}
